<template>
  <div class="cbt">
    <h1 class="hidden">全国一斉CBT模擬試験 成績発表</h1>
    <section class="h2Wrap">
      <h2>成績発表</h2>
      <div class="u-containar-md">
        <div class="pageTitle">{{ setTermTitle() }}</div>
        <CbtLatestScore :score="result.score_new" :maxScore="setMaxScore()" />
        <section class="h3Wrap">
          <h3>成績の推移</h3>
          <div class="graph">
            <CbtResultChart
              :title="graphTitle()"
              :scores="result.scores"
              :dateLabels="result.dateLabels"
              :maxScore="setMaxScore()"
            />
          </div>
        </section>
        <section class="h3Wrap">
          <h3>初回の成績</h3>
          <p class="captionText">
            下記のデータは、初回の点数を基にして算出したものです。
          </p>
          <div class="caption">
            ※ 2回目以降の点数は、順位や平均点には反映されません
          </div>
          <ul class="score">
            <li>
              <div class="head">あなたの得点</div>
              <div class="body">
                <span>{{ result.score }}<span class="unit">点</span></span> /
                {{ setMaxScore() }}点
              </div>
            </li>
            <li>
              <div class="head">総受験者数</div>
              <div class="body">
                <span>{{ result.total }}<span class="unit">人</span></span>
              </div>
            </li>
            <li>
              <div class="head">全受験者の平均点</div>
              <div class="body">
                <span>{{ result.average }}<span class="unit">点</span></span> /
                {{ setMaxScore() }}点
              </div>
            </li>
            <li>
              <div class="head">あなたの全国順位</div>
              <div class="body">
                <span>{{ result.ranking }}<span class="unit">位</span></span> /
                {{ result.total }}人中
              </div>
            </li>
            <li>
              <div class="head">あなたの大学内での順位</div>
              <div class="body">
                <span>{{ result.u_ranking }}<span class="unit">位</span></span>
                / {{ result.u_total }}人中
              </div>
            </li>
          </ul>
        </section>
      </div>
      <div class="btnArea">
        <Button
          :disabled="!activeAllResult"
          :label="'総合成績を見る'"
          lightgray
          :handleClick="() => $router.push(`/magazines/exam/cbt/all/result`)"
        />
        <Button
          :label="'再受験する'"
          primary
          :handleClick="
            () =>
              $router.push(
                `/magazines/exam/cbt/${$route.params.category}/detail/1`
              )
          "
        />
        <Button
          :label="'CBT模擬試験トップへ戻る'"
          :handleClick="() => $router.push('/magazines/exam/cbt')"
        />
      </div>
    </section>
    <section class="h2Wrap">
      <h2>解答・解説</h2>
      <div class="u-containar-md">
        <InputCheckboxSimple
          :name="'incorrectOnly'"
          :label="'誤答した問題に限る'"
          :id="'incorrectOnly'"
          :value="'incorrectOnly'"
          @call-input-change="toggleFilter"
          class="incorrectOnlyCheck"
        />
        <div class="answerCommentary">
          <CbtAnswerCommentaryItem
            v-for="item in filteredData"
            :data="item"
            :key="item"
            class="item"
          />
        </div>
      </div>
      <div class="btnArea">
        <Button
          :disabled="!activeAllResult"
          :label="'総合成績を見る'"
          lightgray
          :handleClick="() => $router.push(`/magazines/exam/cbt/all/result`)"
        />
        <Button
          :label="'再受験する'"
          primary
          :handleClick="
            () =>
              $router.push(
                `/magazines/exam/cbt/${$route.params.category}/detail/1`
              )
          "
        />
        <Button
          :label="'CBT模擬試験トップへ戻る'"
          :handleClick="() => $router.push('/magazines/exam/cbt')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { axiosSetting } from '@/utils/axiosSetting'
import axios from 'axios'
import Button from '@/components/elements/Button.vue'
import InputCheckboxSimple from '@/components/elements/InputCheckboxSimple.vue'
import CbtAnswerCommentaryItem from '@/components/block/CbtAnswerCommentaryItem.vue'
import CbtResultChart from '@/components/block/CbtResultChart.vue'
import CbtLatestScore from '@/components/block/CbtLatestScore.vue'

const parsePastPerformance = (
  pastPerformance: { last_test_day: string; score: number }[]
) => {
  const dateLabels: string[] = []
  const scores: number[] = []
  pastPerformance.forEach((performance) => {
    dateLabels.unshift(performance.last_test_day.split(' ')[0])
    scores.unshift(performance.score)
  })
  return {
    dateLabels,
    scores
  }
}
export default defineComponent({
  name: 'CbtResult',
  components: {
    Button,
    InputCheckboxSimple,
    CbtAnswerCommentaryItem,
    CbtResultChart,
    CbtLatestScore
  },

  setup() {
    // lsの値をクリアする処理
    localStorage.setItem('myAnswers', '')

    // 項名をセット
    const route = useRoute()
    const categoryString = route.params.category + ''
    const categoryNumber = Number(
      categoryString.substring(categoryString.length - 1)
    )
    const setTermTitle = () => {
      const titles = [
        '第一項 基本事項 / 医学一般(30問)',
        '第二項 人体各器官の正常構造と機能、病態、診断、治療(30問)',
        '第三項 全身に及ぶ生理的変化、病態、診断、治療(30問)',
        '第四項 診療の基本 / 社会と医学・医療(30問)',
        '第五項 4連問特集(20問)'
      ]
      return titles[categoryNumber - 1]
    }
    // 満点をセット
    const setMaxScore = () => {
      return categoryNumber !== 5 ? 30 : 20
    }

    const graphTitle = () => {
      if (window.matchMedia('(min-width: 768px)').matches) return setTermTitle()
      const spGraphTitles = [
        '第一項 基本事項 / 医学一般(30問)',
        ['第二項 人体各組織の正常構造と', '機能、病態、診断、治療(30問)'],
        ['第三項 全身に及ぶ生理的変化、', '病態、診断、治療(30問)'],
        ['第四項 診療の基本 / 医学、', '医療と社会(30問)'],
        '第五項 4連問特集(20問)'
      ]
      return spGraphTitles[categoryNumber - 1]
    }

    const result = ref({})
    const answer_commentary = ref([])
    const store = useStore()

    // wpとバックエンドのマージ関数
    const mergedResult: any[] = reactive([])
    const mergeData = (backendCommentary: Array<any>, wpData: Array<any>) => {
      backendCommentary.forEach((bItem) => {
        const targetItem = wpData.find((wItem) => wItem.id == bItem.wpqno)
        if (targetItem) {
          mergedResult.push({ ...targetItem, ...bItem })
        }
      })
    }

    // 解答解説の絞り込み処理
    const filtered = ref(false)
    const toggleFilter = () => {
      filtered.value = !filtered.value
    }
    const filteredData = computed(() => {
      if (filtered.value) {
        return mergedResult.filter((item) => {
          const correctAnswer = item.answers.find(
            (key: any) => key.right_flag === true
          )
          return correctAnswer.answer_check !== item.answer
        })
      } else {
        return mergedResult
      }
    })

    // バックエンド,wpまとめてのapi処理関数
    // wpのapi
    const WP_API_BASE_URL = process.env.VUE_APP_WPAPI_URL || ''
    const WP_API_TIMEOUT = 30000
    const WP_API_BASIC_AUTH = {
      username: process.env.VUE_APP_WPAPI_USER || '',
      password: process.env.VUE_APP_WPAPI_PASS || ''
    }
    const wpDistJson = axios.create({
      baseURL: `${WP_API_BASE_URL}/dist/`,
      auth: WP_API_BASIC_AUTH,
      timeout: WP_API_TIMEOUT
    })

    const ajax = axiosSetting('app', 'action')
    const params = {
      users_id: store.state.userInfo.zip,
      year: '2022',
      term: categoryNumber
    }
    const getData = async () => {
      const promise = []
      // バックエンドapi
      promise[0] = ajax.post('/api/v1/cbt_results', params).then((res: any) => {
        const chartData = parsePastPerformance(res.data.past_performance)
        return { ...res.data, ...chartData }
      })
      promise[1] = wpDistJson.get(`cbt_${categoryNumber}.json`)

      await Promise.all(promise).then((data: any) => {
        for (let i = 0; i < data.length; i++) {
          if (i === 0) {
            result.value = data[i]
            answer_commentary.value = data[i].answer_commentary
          } else {
            mergeData(answer_commentary.value, data[i].data)
          }
        }
      })
    }
    getData()

    const activeAllResult = ref(false)
    const getDataAll = () => {
      ajax.post('/api/v1/cbt_previous_results', params).then((res: any) => {
        activeAllResult.value = res.data.every(
          (item: any) => item.last_test_day !== ''
        )
      })
    }
    getDataAll()

    return {
      setTermTitle,
      graphTitle,
      setMaxScore,
      result,
      answer_commentary,
      mergedResult,
      toggleFilter,
      filteredData,
      activeAllResult
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.cbt {
  letter-spacing: 0.03em;
}

.h2Wrap {
  margin-top: 40px;
  & + & {
    margin-top: 64px;
  }

  h2 {
    background-color: var.$color-sub-bg-g;
    padding: 10px 16px;
    font-size: 2rem;
    line-height: 1.6;
    font-weight: 500;
    @include mixins.media() {
      width: 712px;
      margin-right: auto;
      margin-left: auto;
    }
  }
}

.h3Wrap {
  margin-top: 24px;
}
.pageTitle {
  margin-top: 24px;
  font-weight: 500;
  font-size: 1.8rem;
  line-height: 1.34;
  padding-left: 9px;
  border-left: 4px solid var.$color-main;
}

h3 {
  margin-top: 32px;
  background-color: var.$color-main;
  color: #fff;
  font-size: 1.6rem;
  padding: 2px 16px 4px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0.03em;
  @include mixins.media() {
    margin-top: 40px;
  }
}

.score {
  margin-top: 32px;
  font-weight: 500;
  line-height: 1.7;
  li {
    padding-bottom: 11px;
    border-bottom: 1px solid var.$color-border-gray;
    @include mixins.media() {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 16px;
    }
    + li {
      margin-top: 20px;
      @include mixins.media() {
        margin-top: 16px;
      }
    }
    .body {
      font-size: 1.6rem;
      span {
        color: var.$color-main-text;
        font-size: 2.4rem;
        &.unit {
          font-size: 1.6rem;
        }
      }
    }
  }
}
.captionText {
  margin-top: 16px;
  letter-spacing: 0.03em;
}
.caption {
  margin-top: 12px;
  font-size: 1.2rem;
  line-height: 1.5;
}

.graph {
  margin-top: 24px;
  height: 300px;
  background-color: #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btnArea {
  margin-top: 32px;
  background-color: var.$color-other-bg;
  padding: 24px;
  @include mixins.media() {
    margin-top: 40px;
    background-color: transparent;
    padding: 0;
    display: flex;
    justify-content: center;
    button {
      margin-right: 0;
      margin-left: 0;
    }
  }
  button + button {
    margin-top: 16px;
    @include mixins.media() {
      margin-top: 0;
      margin-left: 16px;
    }
  }
}

.incorrectOnlyCheck {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.answerCommentary {
  margin-top: 24px;
  .item + .item {
    margin-top: 56px;
  }
}
</style>
